import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { QueryClient } from "@tanstack/react-query";

type UseQuotesDeleteOptions = {
  queryClient: QueryClient;
};

export function useQuotesDelete({ queryClient }: UseQuotesDeleteOptions) {
  if (!queryClient) {
    const error = new Error("❌ useQuotesDelete: queryClient is not available");
    console.error(error);
    throw error;
  }

  return useMutation({
    mutationFn: async (id: string) => {
      try {
        // First delete related category associations
        const { error: deleteRelationsError } = await supabase
          .from("quote_categories")
          .delete()
          .eq("quote_id", id);

        if (deleteRelationsError) {
          console.error("Error deleting quote categories:", deleteRelationsError);
          throw new Error(`Failed to delete quote categories: ${deleteRelationsError.message}`);
        }

        // Then delete the quote
        const { error } = await supabase
          .from("quotes")
          .delete()
          .eq("id", id);

        if (error) {
          console.error("Error deleting quote:", error);
          throw new Error(`Failed to delete quote: ${error.message}`);
        }
      } catch (error) {
        console.error("Error in delete mutation:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      toast({
        title: "Success",
        description: "Quote has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      console.error('Mutation error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });
}
